import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MessageService } from "../../../services/message.service";
import { MasterService } from "../../../services/master.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "../../../services/security.service";
import * as words from '../../../services/language'

import { SEARCH_DEBOUNCE_MILLIS, MIN_SEARCH_CHARS, SCROLL_BOTTOM_OUT } from "src/app/constants/list";
import { Contact } from "src/app/models/contact.model";
import { ContactRole } from "src/app/models/contact-role.model";
import { Subscription } from "rxjs";

@Component({
    selector: "app-contacts",
    templateUrl: "./contacts.component.html",
    styleUrls: ["./contacts.component.scss"],
    standalone: false
})
export class ContactsComponent implements OnInit, OnDestroy {

    private FILTER_INQUIRY_KEY = 'flt-cnt-q'
    private FILTER_ROLE_KEY = 'flt-cnt-r'
    protected FILTER_SPAN_KEY = 'flt-cnt-s'

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = words.language

    // Current type filter value.
    protected type: string | null = null

    // save all data by type
    protected contacts: Contact[] = [];

    protected contactIdToDelete: number | null = null

    // define if is loading
    protected loading: boolean = false;

    // Current page the user is viewing (last loaded).
    protected page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    protected exhausted = false

    // Total number of records available
    protected records: number = 0

    // The search form group
    protected form: FormGroup | null = null

    // The current search text to filter the list by.
    private inquiry: string | null = null

    // A debounce timer for search events. Used to wait for
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    // Keep track of how the user wants to sort list.
    private sortables: Object = { 'name': 'ASC' }

    protected roles: ContactRole[] = [];

    protected roleSelected: string = "";

    // Indicates whether or not the contact role is fixed for this list.
    protected fixedRole = false

    // The name of the fixed contact role or null if none.
    protected fixedRoleName = null

    // The UI route path of the fixed contact role (default: 'contacts').
    protected path = 'contacts'

    private subscriptions: Subscription[] = []

    // ************
    // life cycles
    // *************
    constructor(private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, protected store: StoreService, protected security: SecurityService, private fb: FormBuilder) {
        this.form = this.fb.group({
            inquiry: new FormControl<string>('', []),
            role: new FormControl<string>('', []),
            span: new FormControl<string>('', []),
            from: new FormControl<string>('', []),
            to: new FormControl<string>('', [])
        })
    }

    ngOnInit() {
        const path = this.route.snapshot.routeConfig.path
        const pattern = new RegExp('employee', 'i')
        this.fixedRole = (path && pattern.test(path))
        if (this.fixedRole) {
            this.form.get('role').setValue('employee')
            this.fixedRoleName = 'Employees'
            this.path = 'settings/employees'

            this.FILTER_INQUIRY_KEY = 'flt-emp-q'
            this.FILTER_ROLE_KEY = 'flt-emp-r'
            this.FILTER_SPAN_KEY = 'flt-emp-s'
        }

        this.fetchRoles()
        this.readFilters()

        this.subscriptions.push(
            this.form.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (this.inquiry === inquiry) {
                    return
                }

                this.watcher = setTimeout(() => {
                    this.inquiry = inquiry
                    this.writeFilters()
                    this.reset()
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('role').valueChanges.subscribe(role => {
                if (this.fixedRole) {
                    return
                }

                this.writeFilters()
                this.reset()
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('span').valueChanges.subscribe(span => {
                this.writeFilters()
                this.reset()
                this.fetch()
            })
        )

        this.reset()
        this.fetch()
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe()
        }

        this.subscriptions = []

        if (this.watcher !== null) {
            clearTimeout(this.watcher)
            this.watcher = null
        }
    }


    protected fetchRoles() {
        if (this.fixedRole) {
            this.roles = []
            return
        }

        this.master.get(`collections/contacts/roles?display=true`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.roles = res.data.contactRoleTypes
        })
    }

    /**
    * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /*
    * functions
    */
    protected createContact() {
        if (this.path === 'employees') {
            this.router.navigate([`/settings/${this.path}/add`])
            return
        }

        this.router.navigate([`/${this.path}/add`])
        return
    }

    protected setContactIdForDelete = (e: Event, contactId): void => {
        if (!e) {
            return
        }

        this.contactIdToDelete = contactId
    }

    // delete the contact selected
    protected deleteContact = (): void => {
        this.loading = true;
        this.master.discard(`contacts/${this.contactIdToDelete}`, (res) => {
            this.loading = false;

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            // close delete modal
            (document.getElementById("btn-close-modal-delete") as HTMLButtonElement).click();

            this.reset()
            this.fetch()
        })
    }

    /**
    * Handle scroll events of the list container and load
    * the next page when avilable and the user scrolled
    * to the bottom.
    * @param e The scroll event that trigggered this handler.
    */
    protected scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
    *
    * @param field The field that user wants to sort by.
    */
    protected sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables = {}
        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    protected sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    protected sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    protected typeChanged(e) {
        this.type = e
        this.reset()
        this.fetch()
    }
    /**
    * Acquire contact list by given type
    */
    protected fetch = (): void => {
        // if not have permissions, return to account settings
        if (this.security.notPermitted('contacts', 'contact list', 'view')) {
            return
        }

        if (this.loading) { // already getting next page
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        this.loading = true

        let typeParam = ''
        if (this.type && (this.type.length > 0)) {
            typeParam = `&type=${encodeURIComponent(this.type)}`
        }

        let roleParam = ''
        const roleKey = this.form.get('role').value
        if (roleKey && (roleKey.length > 0)) {
            roleParam = `&type=${roleKey}`
        }

        let inquiryParam = ''
        if (this.inquiry && (this.inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        let spanParam = ''
        const spanKey = this.form.get('span').value
        if (spanKey && (spanKey.length > 0)) {
            switch (spanKey) {
                case 'custom':
                    const from = this.form.get('from').value
                    const to = this.form.get('to').value
                    spanParam = `&from=${from}&to=${to}`
                    break
                default:
                    spanParam = `&span=${spanKey}`
                    break
            }
        }

        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }

                sorts.push(`${key}${direction}`)
            }

            sortParam = `&sort=${sorts.join(',')}`
        }

        this.master.get(`contacts?page=${this.page}${typeParam}${inquiryParam}${sortParam}${roleParam}${spanParam}`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.contacts = this.contacts.concat(res.data.contacts)
                return
            }

            this.contacts = res.data.contacts
        })
    }


    // todo: listen to output of navbar
    protected listenerNav = (e): void => {

        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'changeType':
                this.form.get('inquiry').setValue('')
                this.reset()
                this.fetch()
                break
            case 'setPermissions':
                this.reset()
                this.fetch()
                break
            default:
                break
        }
    }

    /**
    * Check session storage for stored filters and apply them if found.
    */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = sessionStorage.getItem(this.FILTER_INQUIRY_KEY)
        if (qFilter && (qFilter.length > 0)) {
            this.form.get('inquiry').setValue(qFilter, { emitEvent: false })
            this.inquiry = qFilter
        }

        const roleFilter = sessionStorage.getItem(this.FILTER_ROLE_KEY)
        if (roleFilter && (roleFilter.length > 0) && !this.fixedRole) {
            this.form.get('role').setValue(roleFilter, { emitEvent: false })
        }

        const spanFilter = sessionStorage.getItem(this.FILTER_SPAN_KEY)
        if (spanFilter && (spanFilter.length > 0)) {
            if (spanFilter.startsWith('custom')) {
                const items = spanFilter.split(';')
                if (!items || (items.length !== 3)) {
                    return
                }

                this.form.get('span').setValue(items[0], { emitEvent: false })
                this.form.get('from').setValue(items[1], { emitEvent: false })
                this.form.get('to').setValue(items[2], { emitEvent: false })

                return
            }

            this.form.get('span').setValue(spanFilter, { emitEvent: false })
        }
    }

    /**
    * Save the existing list filters to session storage for later retrieval.
    */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = this.form.get('inquiry').value
        if (!qFilter || (qFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_INQUIRY_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_INQUIRY_KEY, qFilter)
        }

        const roleFilter = this.form.get('role').value
        if (!roleFilter || (roleFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_ROLE_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_ROLE_KEY, roleFilter)
        }

        const spanFilter = this.form.get('span').value
        if (!spanFilter || (spanFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_SPAN_KEY)
        }
        else {
            let fromTo = ''
            if (spanFilter === 'custom') {
                const from = this.form.get('from').value
                const to = this.form.get('to').value
                fromTo = `;${from};${to}`
            }

            sessionStorage.setItem(this.FILTER_SPAN_KEY, `${spanFilter}${fromTo}`)
        }
    }

    /**
    * Handle date span choices by the user.
    * @param choice
    */
    protected spanned = (choice: Object) => {
        if (!choice || (choice['key'] === undefined) || (choice['key'] === null)) {
            return
        }

        if (choice['key'] === 'custom') {
            const from = choice['from']
            const to = choice['to']
            if (!from || !to) {
                return
            }

            this.form.get('from').setValue(from, { emitEvent: false })
            this.form.get('to').setValue(to, { emitEvent: false })
        }

        this.form.get('span').setValue(choice['key'])
    }
}
